import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, BarChart3, Calendar, Youtube, FileText, Settings, ArrowUpDown, Award, History, Swords } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';
import PlayerCard from '../components/PlayerCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.includes('/api/files/')) {
    const path = url.substring(url.indexOf('/api/files/'));
    return `${BACKEND_URL}${path}`;
  }
  return url;
};

const NAV_LINKS = [
  { href: '/', label: 'HOME' },
  { href: '/votar', label: '🗳️ VOTAR' },
  { href: '/estatisticas', label: 'ESTATÍSTICAS', icon: BarChart3, active: true },
  { href: '/partidas', label: 'PARTIDAS', icon: Calendar },
  { href: '/torneios', label: 'TORNEIOS', icon: Swords },
  { href: '/rankings', label: 'RANKINGS', icon: Trophy },
  { href: '/replays', label: 'REPLAYS', icon: Youtube },
  { href: '/regras', label: 'REGRAS', icon: FileText },
];

const EstatisticasPage = () => {
  // mode: 'mes' (Mês Atual, default) | 'total' (Geral / Todos os Tempos)
  const [mode, setMode] = useState('mes');
  const [players, setPlayers] = useState([]);
  const [history, setHistory] = useState([]);
  const [sortField, setSortField] = useState('pontos_rating');
  const [sortDirection, setSortDirection] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tabela'); // 'tabela' | 'historico'
  const [cardPlayer, setCardPlayer] = useState(null);
  const [hiddenKeys, setHiddenKeys] = useState([]);

  useEffect(() => {
    axios.get(`${API}/rating-config`).then(r => {
      const cfg = r.data?.config || [];
      setHiddenKeys(cfg.filter(c => c.enabled === false).map(c => c.key));
    }).catch(() => {});
    axios.get(`${API}/admin/monthly-history`).then(r => setHistory(r.data.history || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = mode === 'total' ? `${API}/stats/all-time` : `${API}/stats/full`;
    axios.get(url)
      .then(r => setPlayers(r.data.players || []))
      .catch(err => console.error('Erro ao carregar estatísticas:', err))
      .finally(() => setLoading(false));
  }, [mode]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('desc'); }
  };

  const sortedPlayers = [...players].sort((a, b) => {
    if (sortField === 'titles') {
      const aLen = (a.titles || []).length;
      const bLen = (b.titles || []).length;
      return sortDirection === 'asc' ? aLen - bLen : bLen - aLen;
    }
    const aVal = a[sortField] || 0;
    const bVal = b[sortField] || 0;
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const totalGoals = players.reduce((s, p) => s + (p.goals || 0), 0);
  const totalAssists = players.reduce((s, p) => s + (p.assists || 0), 0);
  const totalGames = players.reduce((s, p) => s + (p.games_played || 0), 0);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="estatisticas-page">
      {hiddenKeys.length > 0 && (
        <style>{hiddenKeys.map(k => `[data-col-key="${k}"]{display:none!important}`).join('')}</style>
      )}
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter">⚽ DOMINGÃO</h1>
          <div className="flex items-center gap-2">
            <SubscribeButton />
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin">
              <Settings size={24} />
            </a>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {NAV_LINKS.map(l => {
              const Icon = l.icon;
              const cls = l.active
                ? 'bg-green-500 text-white'
                : 'bg-zinc-100 hover:bg-zinc-200 transition-colors';
              return (
                <a key={l.href} href={l.href} className={`flex-shrink-0 ${cls} px-4 py-2 rounded-lg font-bold text-sm`} data-testid={`nav-${l.href.replace('/', '') || 'home'}`}>
                  {Icon && <Icon className="inline mr-1" size={16} />} {l.label}
                </a>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-black tracking-tight uppercase flex items-center gap-2">
              <BarChart3 size={28} className="text-green-500" /> ESTATÍSTICAS
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              {mode === 'mes' ? 'Mês atual — zera na virada do mês' : 'Todos os tempos — acumula sempre, nunca zera'}
            </p>
          </div>

          {/* Mode toggle (Mês Atual / Geral) */}
          <div className="inline-flex bg-zinc-100 rounded-lg p-1 border-2 border-zinc-200" data-testid="mode-toggle">
            <button
              onClick={() => setMode('mes')}
              className={`px-4 py-2 rounded-md font-black text-sm transition-colors ${mode === 'mes' ? 'bg-green-500 text-white shadow' : 'text-zinc-600 hover:text-zinc-900'}`}
              data-testid="mode-mes"
            >
              📅 MÊS ATUAL
            </button>
            <button
              onClick={() => setMode('total')}
              className={`px-4 py-2 rounded-md font-black text-sm transition-colors ${mode === 'total' ? 'bg-yellow-500 text-white shadow' : 'text-zinc-600 hover:text-zinc-900'}`}
              data-testid="mode-total"
            >
              🏆 GERAL / TOTAL
            </button>
          </div>
        </div>

        {/* Sub-tabs (visible only in Geral mode) */}
        {mode === 'total' && (
          <div className="flex gap-2 mb-5">
            <button
              onClick={() => setTab('tabela')}
              className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${tab === 'tabela' ? 'bg-zinc-950 text-white' : 'bg-white border-2 border-zinc-200 text-zinc-700 hover:bg-zinc-100'}`}
              data-testid="tab-tabela"
            >
              <BarChart3 className="inline mr-1" size={16} /> Tabela Acumulada
            </button>
            <button
              onClick={() => setTab('historico')}
              className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${tab === 'historico' ? 'bg-zinc-950 text-white' : 'bg-white border-2 border-zinc-200 text-zinc-700 hover:bg-zinc-100'}`}
              data-testid="tab-historico"
            >
              <History className="inline mr-1" size={16} /> Histórico Mensal ({history.length})
            </button>
          </div>
        )}

        {(mode === 'mes' || tab === 'tabela') && (
          <>
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider opacity-80">Total Gols</p>
                <p className="text-3xl font-black mt-1">{totalGoals}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider opacity-80">Total Assist.</p>
                <p className="text-3xl font-black mt-1">{totalAssists}</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-500 to-amber-600 text-white rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider opacity-80">Total Jogos</p>
                <p className="text-3xl font-black mt-1">{totalGames}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden" data-testid="stats-table">
              <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
                <h3 className="font-bold text-lg">{mode === 'mes' ? 'Tabela do Mês' : 'Tabela de Todos os Tempos'}</h3>
                <span className="text-xs text-zinc-500">{mode === 'mes' ? 'Zera no fim do mês' : 'Nunca zera'}</span>
              </div>

              {loading ? (
                <div className="p-8 text-center text-zinc-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="text-left p-3 font-bold">#</th>
                        <th className="text-left p-3 font-bold min-w-[160px]">Jogador</th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('pontos_rating')} data-testid="sort-pontos"><div className="flex items-center justify-center gap-1">🏅 Pontos<ArrowUpDown size={14} className={sortField === 'pontos_rating' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('goals')} data-testid="sort-goals" data-col-key="goals"><div className="flex items-center justify-center gap-1">⚽ Golos<ArrowUpDown size={14} className={sortField === 'goals' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('assists')} data-testid="sort-assists" data-col-key="assists"><div className="flex items-center justify-center gap-1">🎯 Assist<ArrowUpDown size={14} className={sortField === 'assists' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('goal_participation')} data-testid="sort-ga"><div className="flex items-center justify-center gap-1">G+A<ArrowUpDown size={14} className={sortField === 'goal_participation' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('shot_accuracy')} data-testid="sort-shot-acc" data-col-key="shots_missed"><div className="flex items-center justify-center gap-1">% Remate<ArrowUpDown size={14} className={sortField === 'shot_accuracy' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('pass_accuracy')} data-testid="sort-pass-acc" data-col-key="passes_failed"><div className="flex items-center justify-center gap-1">% Passe<ArrowUpDown size={14} className={sortField === 'pass_accuracy' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('tackles')} data-testid="sort-tackles" data-col-key="tackles"><div className="flex items-center justify-center gap-1">🦵 Desarm<ArrowUpDown size={14} className={sortField === 'tackles' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('blocks')} data-testid="sort-blocks" data-col-key="blocks"><div className="flex items-center justify-center gap-1">🧱 Bloq<ArrowUpDown size={14} className={sortField === 'blocks' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('clean_sheets')} data-testid="sort-cs" data-col-key="clean_sheets"><div className="flex items-center justify-center gap-1">🛡️ JSG<ArrowUpDown size={14} className={sortField === 'clean_sheets' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('saves')} data-testid="sort-saves" data-col-key="saves"><div className="flex items-center justify-center gap-1">🧤 Defs<ArrowUpDown size={14} className={sortField === 'saves' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('wins')} data-testid="sort-wins" data-col-key="wins"><div className="flex items-center justify-center gap-1">🏆 V<ArrowUpDown size={14} className={sortField === 'wins' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('own_goals')} data-testid="sort-og" data-col-key="own_goals"><div className="flex items-center justify-center gap-1">🎯❌ GC<ArrowUpDown size={14} className={sortField === 'own_goals' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('games_played')} data-testid="sort-games"><div className="flex items-center justify-center gap-1">🕐 J<ArrowUpDown size={14} className={sortField === 'games_played' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                        <th className="p-3 font-bold cursor-pointer hover:bg-zinc-200" onClick={() => handleSort('titles')} data-testid="col-titles"><div className="flex items-center justify-center gap-1"><Award size={14} /> Títulos<ArrowUpDown size={14} className={sortField === 'titles' ? 'text-green-500' : 'text-zinc-400'} /></div></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlayers.map((player, index) => {
                        const imageUrl = getImageUrl(player.profile_image_url);
                        const titles = player.titles || [];
                        return (
                          <tr key={player.name} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${index < 3 ? 'bg-yellow-50/50' : ''}`} data-testid={`row-${player.name}`}>
                            <td className="p-3 font-bold text-zinc-400">{index + 1}</td>
                            <td className="p-3">
                              <button onClick={(e) => { e.preventDefault(); setCardPlayer(player.name); }} className="flex items-center gap-2 hover:underline text-left" data-testid={`profile-link-${player.name}`}>
                                {imageUrl ? (<img src={imageUrl} alt={player.name} className="w-8 h-8 rounded-full object-cover" />) : (<div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold">{player.name.charAt(0)}</div>)}
                                <span className="font-bold truncate">{player.name}</span>
                              </button>
                            </td>
                            <td className="p-3 text-center font-black text-green-700">{(player.pontos_rating || 0).toFixed(1)}</td>
                            <td className="p-3 text-center font-bold text-green-600" data-col-key="goals">{player.goals || 0}</td>
                            <td className="p-3 text-center font-bold text-blue-600" data-col-key="assists">{player.assists || 0}</td>
                            <td className="p-3 text-center font-bold text-orange-600">{player.goal_participation || 0}</td>
                            <td className="p-3 text-center" data-col-key="shots_missed"><span className={`font-bold ${player.shot_accuracy >= 50 ? 'text-green-600' : player.shot_accuracy > 0 ? 'text-yellow-600' : 'text-zinc-400'}`}>{player.shot_accuracy || 0}%</span></td>
                            <td className="p-3 text-center" data-col-key="passes_failed"><span className={`font-bold ${player.pass_accuracy >= 70 ? 'text-green-600' : player.pass_accuracy > 0 ? 'text-yellow-600' : 'text-zinc-400'}`}>{player.pass_accuracy || 0}%</span></td>
                            <td className="p-3 text-center text-zinc-600" data-col-key="tackles">{player.tackles || 0}</td>
                            <td className="p-3 text-center text-zinc-600" data-col-key="blocks">{player.blocks || 0}</td>
                            <td className="p-3 text-center text-zinc-600" data-col-key="clean_sheets">{player.clean_sheets || 0}</td>
                            <td className="p-3 text-center text-zinc-600" data-col-key="saves">{player.saves || 0}</td>
                            <td className="p-3 text-center font-bold text-green-600" data-col-key="wins">{player.wins || 0}</td>
                            <td className="p-3 text-center text-red-600" data-col-key="own_goals">{player.own_goals || 0}</td>
                            <td className="p-3 text-center font-bold text-purple-600">{player.games_played || 0}</td>
                            <td className="p-3 text-center" data-testid={`titles-${player.name}`}>
                              {titles.length === 0 ? (
                                <span className="text-zinc-400 text-xs">—</span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 font-black px-2.5 py-0.5 rounded-full border border-yellow-300"
                                  title={titles.join(' · ')}
                                >
                                  <Award size={12} /> {titles.length}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-4 p-4 bg-zinc-100 rounded-xl text-xs text-zinc-600">
              <p className="font-bold mb-2">Sobre esta tabela:</p>
              <p>
                {mode === 'mes'
                  ? 'Esta é a competição do mês corrente. No fim do mês, os totais são acumulados na tabela "Geral / Total" e as estatísticas mensais zeram.'
                  : 'Esta é a soma de todos os meses já fechados + o mês atual. Os dados nunca zeram.'}
                {' '}O <strong>Pontos_Rating</strong> é calculado dinamicamente pela fórmula oficial.
              </p>
              <p className="mt-2"><a href="/pontuacao" className="text-indigo-600 underline font-bold" data-testid="link-pontuacao">📖 Ver como funciona a pontuação</a></p>
            </div>
          </>
        )}

        {mode === 'total' && tab === 'historico' && (
          <div className="space-y-4" data-testid="history-list">
            {history.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-zinc-200 p-8 text-center text-zinc-500">
                Nenhum mês foi fechado ainda. Quando o admin fechar um mês, os campeões aparecem aqui.
              </div>
            ) : (
              history.map((snap) => (
                <div key={snap.id} className="bg-white rounded-xl border-2 border-zinc-200 p-5" data-testid={`history-${snap.period_label}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-black uppercase">{snap.period_label}</h3>
                    <span className="text-xs text-zinc-500">{snap.snapshot_date}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {snap.top_performers?.artilheiro && (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                        <p className="text-[10px] font-bold uppercase text-green-700">⚽ Artilheiro do Mês</p>
                        <p className="font-black text-sm truncate">{snap.top_performers.artilheiro.name}</p>
                        <p className="text-xs text-green-700 font-bold">{snap.top_performers.artilheiro.value} gols</p>
                      </div>
                    )}
                    {snap.top_performers?.garcom && (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                        <p className="text-[10px] font-bold uppercase text-blue-700">🎯 Rei das Assistências</p>
                        <p className="font-black text-sm truncate">{snap.top_performers.garcom.name}</p>
                        <p className="text-xs text-blue-700 font-bold">{snap.top_performers.garcom.value} assist.</p>
                      </div>
                    )}
                    {snap.top_performers?.mvp && (
                      <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-3">
                        <p className="text-[10px] font-bold uppercase text-purple-700">⭐ MVP do Mês</p>
                        <p className="font-black text-sm truncate">{snap.top_performers.mvp.name}</p>
                        <p className="text-xs text-purple-700 font-bold">{snap.top_performers.mvp.value} ⭐</p>
                      </div>
                    )}
                    {snap.top_performers?.relogio_de_ouro && (
                      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3">
                        <p className="text-[10px] font-bold uppercase text-yellow-700">🕐 Homem da Presença</p>
                        <p className="font-black text-sm truncate">{snap.top_performers.relogio_de_ouro.name}</p>
                        <p className="text-xs text-yellow-700 font-bold">{snap.top_performers.relogio_de_ouro.value} jogos</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
      {cardPlayer && <PlayerCard playerName={cardPlayer} onClose={() => setCardPlayer(null)} />}
    </div>
  );
};

export default EstatisticasPage;
